import { BaseComponent } from "./base_component"
import { NodeData, WorkerInputs, WorkerOutputs } from 'rete/types/core/data'
import { Node, Output } from 'rete'
import { booleanDataSocket, numericDataSocket } from "../socket_types"
import { ProjectProperties } from "."

import { maskFromExtentAndShape } from "../bounding_box"
import { createXYZ } from "ol/tilegrid"
import { retrieveModelDataWCS } from "../model_retrieval"
import { BooleanTileGrid, NumericTileGrid } from "../tile_grid"
import { TypedArray } from "d3"
import { CheckboxSelectControl } from "../controls/checkbox-select"

// Generic component class for handling raster datasets

export interface RasterComponentOption {
    name: string
    source: string
    area?: string
    unit?: string
    // If the raster is a boolean, the number is the value that is true
    bool?: number
    non_values?: number[]
    trim_below?: number
    option?: string | string[]
    transform?: (val: number) => number
}

export class RasterComponent extends BaseComponent {
    projectProps: ProjectProperties
    options: RasterComponentOption[]
    cache: Map<string, NumericTileGrid | BooleanTileGrid>
    optionLabels: string[] | undefined
    
    constructor(projectProps: ProjectProperties, name: string, category: string, options: RasterComponentOption[], optionLabel?: string[]) {
        super(name)
        this.category = category
        this.projectProps = projectProps
        this.options = options
        this.cache = new Map()
        this.optionLabels = optionLabel
    }

    getAvailableOptionsForDimension(node: Node, dimensionIndex: number): string[] {
        // Get currently selected values for previous dimensions by iteratively building them up
        const previousSelections: (string[] | undefined)[] = []
        for (let i = 0; i < dimensionIndex; i++) {
            // For each previous dimension, get available options based on selections before it
            let matchingOptions = this.options
            for (let j = 0; j < i; j++) {
                const prevValues = previousSelections[j]
                if (prevValues !== undefined && prevValues.length > 0) {
                    matchingOptions = matchingOptions.filter(opt => {
                        if (Array.isArray(opt.option)) {
                            return prevValues.includes(opt.option[j])
                        } else if (opt.option !== undefined && j === 0) {
                            return prevValues.includes(opt.option)
                        }
                        return false
                    })
                }
            }
            
            // Get unique values for dimension i from matching options
            const availableValues = matchingOptions.map(opt => {
                if (Array.isArray(opt.option)) {
                    return opt.option[i]
                } else if (opt.option !== undefined && i === 0) {
                    return opt.option
                }
                return undefined
            }).filter((v): v is string => typeof v === "string")
            const uniqueValues = Array.from(new Set(availableValues))
            
            // Get the selected values for this dimension (now an array)
            const selectedIndices = node.data[`option${i}`] as string[] | undefined
            if (selectedIndices && Array.isArray(selectedIndices) && selectedIndices.length > 0) {
                const selectedValues = selectedIndices
                    .map(idx => {
                        const index = Number(idx)
                        if (index >= 0 && index < uniqueValues.length) {
                            return uniqueValues[index]
                        }
                        return undefined
                    })
                    .filter((v): v is string => v !== undefined)
                previousSelections.push(selectedValues.length > 0 ? selectedValues : undefined)
            } else {
                previousSelections.push(undefined)
            }
        }

        // Filter options that match any of the previous selections
        let matchingOptions = this.options
        for (let i = 0; i < dimensionIndex; i++) {
            const prevValues = previousSelections[i]
            if (prevValues !== undefined && prevValues.length > 0) {
                matchingOptions = matchingOptions.filter(opt => {
                    if (Array.isArray(opt.option)) {
                        return prevValues.includes(opt.option[i])
                    } else if (opt.option !== undefined && i === 0) {
                        return prevValues.includes(opt.option)
                    }
                    return false
                })
            }
        }

        // Extract unique values for this dimension from matching options
        const values = matchingOptions.map(opt => {
            if (Array.isArray(opt.option)) {
                return opt.option[dimensionIndex]
            } else if (opt.option !== undefined && dimensionIndex === 0) {
                return opt.option
            }
            return undefined
        }).filter((v): v is string => typeof v === "string")
        
        return Array.from(new Set(values))
    }

    loadOutputs(node: Node) {
        const option_vals: (string[] | undefined)[] = []

        // Get the number of option dimensions by finding the max array length
        const optionLengths = this.options.map(opt => {
            if (Array.isArray(opt.option)) {
                return opt.option.length
            } else if (opt.option !== undefined) {
                return 1
            }
            return 0
        })
        const maxOptionLength = optionLengths.length > 0 ? Math.max(...optionLengths) : 0

        // Extract selected values for each option dimension using available options
        for (let i = 0; i < maxOptionLength; i++) {
            const availableValues = this.getAvailableOptionsForDimension(node, i)
            const selectedIndices = node.data[`option${i}`] as string[] | undefined
            
            if (selectedIndices && Array.isArray(selectedIndices) && selectedIndices.length > 0) {
                // Convert indices to actual values
                const selectedValues = selectedIndices
                    .map(idx => {
                        const index = Number(idx)
                        if (index >= 0 && index < availableValues.length) {
                            return availableValues[index]
                        }
                        return undefined
                    })
                    .filter((v): v is string => v !== undefined)
                option_vals.push(selectedValues.length > 0 ? selectedValues : undefined)
            } else {
                option_vals.push(undefined)
            }
        }

        // Remove all existing outputs
        Array.from(node.outputs.entries()).forEach(([key, output]) => {
            output.connections.forEach(connection => {
                connection.remove()
            })
            node.removeOutput(output)
        })

        // Filter options based on matching any selected value in each option dimension
        // If nothing is selected for a dimension, that dimension matches everything
        if (option_vals.length > 0 && option_vals.some(v => v !== undefined && v.length > 0)) {
            this.options.filter(opt => {
                if (Array.isArray(opt.option)) {
                    return opt.option.every((val, i) => {
                        const selectedVals = option_vals[i]
                        // If nothing selected for this dimension, don't match
                        if (!selectedVals || selectedVals.length === 0) {
                            return false
                        }
                        return selectedVals.includes(val)
                    })
                } else if (opt.option !== undefined) {
                    const selectedVals = option_vals[0]
                    if (!selectedVals || selectedVals.length === 0) {
                        return false
                    }
                    return selectedVals.includes(opt.option)
                }
                return false
            }).forEach((option) => {
                const units = option.bool ? '' : (option.unit && option.area ? `[[${option.unit}/${option.area}]]` : (option.unit ? `[[${option.unit}]]` : ""))
                node.addOutput(new Output(option.name, `${option.name} ${units}`, option.bool ? booleanDataSocket : numericDataSocket))
            })
        }else if (maxOptionLength === 0) {
            // there are no options to filter, so show all outputs
            this.options.forEach((option) => {
                const units = option.bool ? '' : (option.unit && option.area ? `[[${option.unit}/${option.area}]]` : (option.unit ? `[[${option.unit}]]` : ""))
                node.addOutput(new Output(option.name, `${option.name} ${units}`, option.bool ? booleanDataSocket : numericDataSocket))
            })
        }

        node.update()
    }

    async builder(node: Node) {

        for (let key in node.data) {
            // Backwards compatibility for old node data
            if (key.startsWith('option')) {
                if (key === 'optionId'){
                    node.data['option0'] = [node.data[key]]
                }
                if (!Array.isArray(node.data[key])) {
                    node.data[key] = [node.data[key]]
                }
            }
        }
        // Get the number of option dimensions by finding the max array length
        const optionLengths = this.options.map(opt => {
            if (Array.isArray(opt.option)) {
                return opt.option.length
            } else if (opt.option !== undefined) {
                return 1
            }
            return 0
        })
        const maxOptionLength = optionLengths.length > 0 ? Math.max(...optionLengths) : 0

        // Create SelectControls for each option dimension
        for (let i = 0; i < maxOptionLength; i++) {
            const optionLabel = this.optionLabels?.[i] ?? `Option ${i + 1}`

            if (node.data[`option${i}`] === undefined) {
                node.data[`option${i}`] = i == 0 ? [i] : Array.from({ length: this.getAvailableOptionsForDimension(node, i).length }, (_, i) => 0 + i)
            }
            
            // Create a change handler that resets subsequent options and reloads outputs
            const changeHandler = () => {
                // Increment a counter to force React to re-render subsequent controls
                // This works because React uses keys, and changing node data triggers updates
                const updateCounter = (node.data['_optionUpdateCounter'] as number || 0) + 1
                node.data['_optionUpdateCounter'] = updateCounter
                
                this.loadOutputs(node)
                node.update()
                
                // Force update by triggering editor events
                if (this.editor) {
                    this.editor.trigger('process')
                }
            }
            
            node.addControl(new CheckboxSelectControl(
                this.editor,
                `option${i}`,
                () => {
                    // Access the update counter to ensure this function is called when it changes
                    const _ = node.data['_optionUpdateCounter']
                    const availableValues = this.getAvailableOptionsForDimension(node, i)
                    return availableValues.map((value, j) => ({name: value, id: j}))
                },
                changeHandler,
                optionLabel
            ))
        }

        this.loadOutputs(node)
    }

    async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
        const editorNode = this.editor?.nodes.find(n => n.id === node.id)
        if (editorNode === undefined) { return }

        // Get the number of option dimensions by finding the max array length
        const optionLengths = this.options.map(opt => {
            if (Array.isArray(opt.option)) {
                return opt.option.length
            } else if (opt.option !== undefined) {
                return 1
            }
            return 0
        })
        const maxOptionLength = optionLengths.length > 0 ? Math.max(...optionLengths) : 0

        // Extract selected values for each option dimension using available options
        // Note: We need to create a temporary node-like object for getAvailableOptionsForDimension
        const tempNode = { data: node.data } as Node
        const option_vals: (string[] | undefined)[] = []
        for (let i = 0; i < maxOptionLength; i++) {
            const availableValues = this.getAvailableOptionsForDimension(tempNode, i)
            const selectedIndices = node.data[`option${i}`] as string[] | undefined
            
            if (selectedIndices && Array.isArray(selectedIndices) && selectedIndices.length > 0) {
                // Convert indices to actual values
                const selectedValues = selectedIndices
                    .map(idx => {
                        const index = Number(idx)
                        if (index >= 0 && index < availableValues.length) {
                            return availableValues[index]
                        }
                        return undefined
                    })
                    .filter((v): v is string => v !== undefined)
                option_vals.push(selectedValues.length > 0 ? selectedValues : undefined)
            } else {
                option_vals.push(undefined)
            }
        }

        const mask = await maskFromExtentAndShape(this.projectProps)

        const tileGrid = createXYZ()
        const outputTileRange = tileGrid.getTileRangeForExtentAndZ(this.projectProps.extent, this.projectProps.zoom)

        const promises = this.options.filter(opt => {
            if (maxOptionLength === 0) return true
            if (option_vals.length > 0 && option_vals.some(v => v !== undefined && v.length > 0)) {
                if (Array.isArray(opt.option)) {
                    return opt.option.every((val, i) => {
                        const selectedVals = option_vals[i]
                        // If nothing selected for this dimension, don't match
                        if (!selectedVals || selectedVals.length === 0) {
                            return false
                        }
                        return selectedVals.includes(val)
                    })
                } else if (opt.option !== undefined) {
                    const selectedVals = option_vals[0]
                    if (!selectedVals || selectedVals.length === 0) {
                        return false
                    }
                    return selectedVals.includes(opt.option)
                }
                return false
            }
            return false
        }).filter(
            opt => node.outputs[opt.name] && node.outputs[opt.name].connections.length > 0 
        ).map(
            async opt => {
                const optionKey = Array.isArray(opt.option) ? opt.option.join('_') : (opt.option ?? 'default')
                const cacheKey = opt.source + '_' + optionKey + '_' + opt.name
                console.log(cacheKey)
                if (this.cache.has(cacheKey)) {
                    console.log('cache hit')
                    outputs[opt.name] = this.cache.get(cacheKey)
                    
                }else{

                    const geotiff = await retrieveModelDataWCS(this.projectProps.extent, opt.source, outputTileRange)

                    const image = await geotiff.getImage()
                    const rasters = await geotiff.readRasters({ bbox: this.projectProps.extent, width: outputTileRange.getWidth(), height: outputTileRange.getHeight() })
                    
                    const out = opt.bool ? new BooleanTileGrid(this.projectProps.zoom, outputTileRange.minX, outputTileRange.minY, outputTileRange.getWidth(), outputTileRange.getHeight()) : new NumericTileGrid(this.projectProps.zoom, outputTileRange.minX, outputTileRange.minY, outputTileRange.getWidth(), outputTileRange.getHeight())
                    
                    for (let i = 0; i < (rasters[0] as TypedArray).length; i++) {
                        let x = (outputTileRange.minX + i % image.getWidth()) 
                        let y = (outputTileRange.minY + Math.floor(i / image.getWidth()))

                        let val = rasters[0][i]

                        if (opt.transform) {
                            val = opt.transform(val)
                        }

                        if (opt.non_values && opt.non_values.includes(val)) {
                            val = NaN
                        }

                        if (opt.trim_below && val < opt.trim_below) {
                            val = NaN
                        }

                        if (opt.bool) {
                            (out as BooleanTileGrid).set(x, y, mask.get(x, y) === true ? (val === opt.bool) : false)
                        } else {
                            (out as NumericTileGrid).set(x, y, mask.get(x, y) === true ? (val) : NaN)
                        }
                    }

                    if (opt.unit && out instanceof NumericTileGrid) {
                        (out as NumericTileGrid).properties.unit = opt.unit
                    }
                    if (opt.area && out instanceof NumericTileGrid) {
                        (out as NumericTileGrid).properties.area = opt.area
                    }

                    outputs[opt.name] = out
                    this.cache.set(cacheKey, out)

                }
                
            }
        )

        await Promise.all(promises)
    }

}

